import { useMutation, type UseMutationResult } from "@tanstack/react-query";
import notification from "@app/services/notification";
import {
    usePerformBoOperationMutation,
    type PerformBoOperationVariables,
} from "@src/businessModules/WorkShop/shared/bo/usePerformBoOperationMutation";
import { onsideInvoicesServices, type OnsideInvoiceContentConfirmLeaveParams } from "../../../../api/onsideInvoices";

/** Параметры хука мутаций карточки отгрузки СГП. */
export interface UseStockFinishedGoodsShipmentCardMutationsParams {
    /**
     * Колбэк синхронизации обновлённой строки НС после смены признака отгрузки.
     * @param contentCode код строки состава НС
     */
    onContentRefetch?: (contentCode: number) => void | Promise<unknown>;
    /** Колбэк успешной BO-операции. */
    onBoOperationSuccess?: () => void;
}

/** Мутации карточки отгрузки СГП. */
export interface UseStockFinishedGoodsShipmentCardMutationsResult {
    /** Установка/снятие признака подтверждения отгрузки строки НС. */
    setConfirmLeaveMutation: UseMutationResult<void, Error, OnsideInvoiceContentConfirmLeaveParams>;
    /** Выполнение BO-операции над НС. */
    performBoOperationMutation: UseMutationResult<void, Error, PerformBoOperationVariables>;
}

/** Screen-level мутации карточки отгрузки готовой продукции со склада СГП. */
export function useStockFinishedGoodsShipmentCardMutations({
    onContentRefetch,
    onBoOperationSuccess,
}: UseStockFinishedGoodsShipmentCardMutationsParams): UseStockFinishedGoodsShipmentCardMutationsResult {
    const setConfirmLeaveMutation = useMutation({
        mutationKey: ["stock", "finishedGoodsShipment", "setConfirmLeave"],
        meta: { errorSource: "stock.finishedGoodsShipment.setConfirmLeave" },
        mutationFn: (variables: OnsideInvoiceContentConfirmLeaveParams) =>
            onsideInvoicesServices.content.setConfirmLeave(variables),
        onSuccess: async (_data, variables) => {
            notification.success(
                variables.confirmLeave ? "Подтверждение отгрузки строки сохранено" : "Подтверждение отгрузки строки снято",
            );
            await onContentRefetch?.(variables.code);
        },
    });

    const performBoOperationMutation = usePerformBoOperationMutation({
        scope: "stockFinishedGoodsShipment",
        onSuccess: onBoOperationSuccess,
    });

    return {
        setConfirmLeaveMutation,
        performBoOperationMutation,
    };
}
